import { CalendarDate, CalendarDateSerialized } from "@sp-crm/core";

type TourDateValue = string | CalendarDateSerialized;

export const parseTourDate = (dateValue: TourDateValue): CalendarDate | null => {
    const parsed = CalendarDate.parse(dateValue as string);
    return parsed.hasValue ? parsed.get() : null;
};

const parseTourTime = (
    timeStr: string | null | undefined,
): { hours: number; minutes: number } | null => {
    if (!timeStr) return null;
    const [h, m] = timeStr.split(":").map(Number);
    if (isNaN(h) || isNaN(m)) return null;
    return { hours: h, minutes: m };
};

const timeIsBeforeNow = (
    tourTime: { hours: number; minutes: number },
    nowHours: number,
    nowMinutes: number,
): boolean => {
    if (tourTime.hours < nowHours) return true;
    if (tourTime.hours === nowHours && tourTime.minutes < nowMinutes) return true;
    return false;
};

export const splitTours = <
    T extends { tourDate: TourDateValue; tourTime?: string | null },
>(
    tours: T[],
    now: Date = new Date(),
): { upcoming: T[]; past: T[] } => {
    const today = CalendarDate.fromLocalDate(now);
    const nowHours = now.getHours();
    const nowMinutes = now.getMinutes();
    const upcoming: T[] = [];
    const past: T[] = [];

    for (const tour of tours) {
        const tourDate = parseTourDate(tour.tourDate);
        if (!tourDate) {
            upcoming.push(tour);
            continue;
        }
        if (tourDate.lessThan(today)) {
            past.push(tour);
        } else if (today.lessThan(tourDate)) {
            upcoming.push(tour);
        } else {
            const time = parseTourTime(tour.tourTime);
            if (time && timeIsBeforeNow(time, nowHours, nowMinutes)) {
                past.push(tour);
            } else {
                upcoming.push(tour);
            }
        }
    }

    const compareAsc = (a: T, b: T): number => {
        const dateA = parseTourDate(a.tourDate);
        const dateB = parseTourDate(b.tourDate);
        if (!dateA || !dateB) return 0;
        if (dateA.lessThan(dateB)) return -1;
        if (dateB.lessThan(dateA)) return 1;
        const timeA = parseTourTime(a.tourTime);
        const timeB = parseTourTime(b.tourTime);
        if (!timeA && !timeB) return 0;
        if (!timeA) return 1;
        if (!timeB) return -1;
        if (timeA.hours !== timeB.hours) return timeA.hours - timeB.hours;
        return timeA.minutes - timeB.minutes;
    };

    upcoming.sort(compareAsc);
    past.sort((a, b) => -compareAsc(a, b));

    return { upcoming, past };
};

export const formatTourDate = (dateValue: TourDateValue): string => {
    const parsed = CalendarDate.parse(dateValue as string);
    if (!parsed.hasValue) {
        return "Unknown date";
    }
    return parsed.get().toDate().toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
    });
};

export const formatTourTime = (timeStr: string | null | undefined): string => {
    if (!timeStr) return "";
    const [hours, minutes] = timeStr.split(":");
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;
    return ` at ${hour12}:${minutes} ${ampm}`;
};
